import React, { useEffect, useState } from 'react';
import { Course, Schedule } from 'models/model';
import { ScheduleExport, ScheduleGetPaging } from 'services/scheduleService';
import {
	CourseExportGA,
	CourseExportReport,
	CourseGet,
	CourseTeachingDelete,
	CourseTeachingSave,
	CourseVehicleDelete,
	CourseVehicleSave,
	ExportTeachingAssignment,
	ExportResultTracking,
	ExportDecision,
} from 'services/courseService';
import {
	StudentDownloadTemplate,
	StudentExport,
	StudentGetPaging,
	StudentImport,
} from 'services/studentService';
import { TeacherGetPaging } from 'services/teacherService';
import { VehicleGetPaging } from 'services/vehicleService';
import ScheduleModal from 'components/SaveScheduleModal';
import BulkSelectModal from 'components/SelectabItem';
import { useConfirm } from 'popup/ConfirmContext';
import { formatDate } from 'lib/utils';
import SaveStudentModal from 'components/SaveStudentModal';
import {
	Car,
	Download,
	Edit,
	Files,
	PencilLine,
	Plus,
	Trash,
	Upload,
	UserRoundPen,
	Link,
} from 'lucide-react';
import {
	ExamCheck,
	ExamCreateAutomatic,
	ExamDelete,
	ExamGetPaging,
} from 'services/examService';
import SaveExamModal from 'components/SaveExamModal';
import {
	SemesterDelete,
	SemesterDuplicate,
	SemesterExport,
	SemesterGetCourse,
	SemesterSave,
} from 'services/semesterService';
import SubjectPlanningPage from 'components/SubjectPlanningPage';
import ErrorModal from 'popup/ErrorModal';
import toast from 'react-hot-toast';
import DocumentManager from 'components/DocumentManager';
import ExamResultsTab from 'components/ExamResultsTab';
import ExportGAModal from 'components/ExportGAModal';
import {
	ExamResultExport,
	ExamResultGetList,
	ExamResultImport,
	ExamResultSaveList,
} from 'services/examResultService';
import { SettingGet } from 'services/settingService';
import LoadingOverlay from 'components/Static/LoadingOverlay';
import DownloadingOverlay from 'components/Static/DownloadingOverlay';
import MinaScheduler from '../../../../components/MinaScheduler';
import { useParams } from 'react-router-dom';
import { LogGetPaging } from '../../../../services/logService';
import moment from 'moment-timezone';
import ExportAllModal, { ExportAllModalProps } from '../../../../components/ExportAllModal';
import LinkTeacherVehicleModal from '../../../../components/LinkTeacherVehicleModal';
import ExportResultTrackingModal from '../../../../components/ExportResultTrackingModal';
import ExportDecisionModal from '../../../../components/ExportDecisionModal';

type TabType =
	| 'schedule'
	| 'students'
	| 'teachers'
	| 'vehicles'
	| 'documents'
	| 'exams'
	| 'examResults'
	| 'trainingPlan'
	| 'log';

interface DataStatus {
	schedule: boolean;
	students: boolean;
	teachers: boolean;
	vehicles: boolean;
	documents: boolean;
	exams: boolean;
	examResults: boolean;
	trainingPlan: boolean;
	log: boolean;
}

export default function ScheduleSchedulePage() {
	const { courseId }: any = useParams();
	const [dataStatus, setDataStatus] = useState<DataStatus>({
		schedule: false,
		students: false,
		teachers: false,
		vehicles: false,
		documents: false,
		exams: false,
		examResults: false,
		trainingPlan: false,
		log: false,
	});
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [courseLoaded, setCourseLoaded] = useState(false);
	const [schedule, setSchedule] = useState<Schedule[]>([]);
	const [course, setCourse] = useState<Course | null>(null);
	const [students, setStudents] = useState<any[]>([]);
	const [teachers, setTeachers] = useState<any[]>([]);
	const [vehicles, setVehicles] = useState<any[]>([]);
	const [exams, setExams] = useState<any[]>([]);
	const [examResults, setExamResults] = useState<any[]>([]);
	const [examTypes, setExamTypes] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<TabType>('schedule');
	const [trainingPlanData, setTrainingPlanData] = useState<any>(null);
	const [logs, setLogs] = useState<any>([]);

	const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
	const [selectedStudent, setSelectedStudent] = useState<any>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredStudents, setFilteredStudents] = useState<any[]>([]);

	const [isExamModalOpen, setIsExamModalOpen] = useState(false);
	const [selectedExam, setSelectedExam] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
		null
	);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isExportGAModalOpen, setIsExportGAModalOpen] = useState(false);
	const [isExportAllModalOpen, setIsExportAllModalOpen] = useState(false);
	const [isExportResultTrackingModalOpen, setIsExportResultTrackingModalOpen] = useState(false);
	const [isExportDecisionModalOpen, setIsExportDecisionModalOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [exportingAssignment, setExportingAssignment] = useState(false);

	const openCreateModal = () => {
		setSelectedSchedule(null);
		setIsModalOpen(true);
	};

	const openEditModal = (schedule: Schedule) => {
		setSelectedSchedule(schedule);
		setIsModalOpen(true);
	};

	const [isBulkSelectModalOpen, setIsBulkSelectModalOpen] = useState(false);
	const [modalType, setModalType] = useState<'teachers' | 'vehicles'>(
		'teachers'
	);
	const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
	const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
	const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

	const [allTeachers, setAllTeachers] = useState<any[]>([]);
	const [allTeacherNoEligible, setAllTeacherNoEligible] = useState<any[]>([]);

	const [allVehicles, setAllVehicles] = useState<any[]>([]);
	const [allVehicleNoEligible, setAllVehicleNoEligible] = useState<any[]>([]);

	const fetchAllTeachers = async () => {
		const res = await TeacherGetPaging({
			licenseClassId: course?.licenseClassId,
			isAddCourse: true,
			courseId: courseId,
		}); // Không truyền courseId để lấy tất cả
		setAllTeachers(res.result.items);
		const r = await TeacherGetPaging({
			licenseClassId: course?.licenseClassId,
			isAddCourse: true,
			isEligible: false,
			courseId: courseId,
		});
		setAllTeacherNoEligible(r.result.items);
	};

	const fetchAllVehicles = async () => {
		const res = await VehicleGetPaging({
			licenseClassId: course?.licenseClassId,
			isAddCourse: true,
			courseId: courseId,
		}); // Không truyền courseId để lấy tất cả
		setAllVehicles(res.result.items);
		const r = await VehicleGetPaging({
			licenseClassId: course?.licenseClassId,
			isAddCourse: true,
			isEligible: false,
			courseId: courseId,
		});
		setAllVehicleNoEligible(r.result.items);
	};
	const { confirm } = useConfirm();

	const handleDeleteItem = async (id: string, type?: any) => {
		try {
			let res: any = null;
			if (type === 'teacher') {
				const isConfirmed = await confirm({
					title: 'Xác nhận xóa giáo viên',
					description:
						'Bạn có chắc chắn muốn xóa giáo viên này khỏi khóa học?',
				});

				if (!isConfirmed) return;

				res = await CourseTeachingDelete({
					courseId,
					id,
				});
				fetchTeachers();
			}

			if (type === 'vehicle') {
				const isConfirmed = await confirm({
					title: 'Xác nhận xóa xe',
					description:
						'Bạn có chắc chắn muốn xóa xe này khỏi khóa học?',
				});

				if (!isConfirmed) return;

				res = await CourseVehicleDelete({
					courseId,
					id,
				});
				fetchVehicles();
			}

			if (type === 'exam') {
				const isConfirmed = await confirm({
					title: 'Xác nhận xóa bài thi',
					description: 'Bạn có chắc chắn muốn xóa bài thi này?',
				});

				if (!isConfirmed) return;

				res = await ExamDelete(id);
				fetchExams();
			}

			if (res.message) {
				toast.success(res.message);
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		}
	};

	const handleStudentImport = async (file: File) => {
		try {
			setIsLoading(true);
			await StudentImport(courseId, file);

			fetchStudents();
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsLoading(false);
		}
	};

	const handleExamResultImport = async (file: File) => {
		try {
			setIsLoading(true);
			const res = await ExamResultImport(courseId, file);

			fetchExamResults();
			toast.success(res.message);
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDownloadTemplate = async () => {
		try {
			const blob = await StudentDownloadTemplate();

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', 'Import-Student.xlsx');
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success('Tải xuống thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		}
	};

	const handleStudentExport = async () => {
		try {
			setIsExporting(true);

			const blob = await StudentExport(courseId);

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', `DSHV-${courseId}.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			setIsExporting(false);
			toast.success('Tải xuống thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportSchedule = async () => {
		try {
			setIsExporting(true);
			const blob = await ScheduleExport(courseId);

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', `TienDo_${courseId}.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);

			setIsExporting(false);
			toast.success('Tải xuống thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExamResultSave = async (saveData: any) => {
		try {
			setIsLoading(true);
			const response = await ExamResultSaveList(courseId, saveData);

			if (response.message) {
				toast.success(response.message);
			}
			await fetchExamResults();
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi lưu kết quả thi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
			console.error('Lỗi khi lưu kết quả thi:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleExportResult = async () => {
		try {
			setIsExporting(true);
			const blob = await ExamResultExport(courseId);

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', `BangDiem_${courseId}.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			setIsExporting(false);
			toast.success('Tải xuống thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportGA = async (memberCount: number) => {
		try {
			setIsExporting(true);
			const blob = await CourseExportGA({
				courseId,
				memberCount,
			});

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute(
				'download',
				`GiaoAn_${course?.classCode || courseId}.zip`
			);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success('Tải xuống giáo án thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xuất giáo án';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};
	const handleExportAll = async (option: ExportAllModalProps) => {
		try {
			setIsExporting(true);
			const blob = await CourseExportReport({
				...option,
				courseId
			});

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute(
				'download',
				`Bao_cao_${course?.classCode || courseId}.zip`
			);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success('Tải xuống giáo án thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xuất giáo án';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportTeachingAssignment = async () => {
		setExportingAssignment(true);
		try {
			const blob = await ExportTeachingAssignment({ id: courseId });

			// Create download link
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `Phan_Cong_GV_Xe_${course?.classCode || courseId}.docx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			toast.success('Tải xuống danh sách phân công thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải xuống';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setExportingAssignment(false);
		}
	};

	const handleExportResultTracking = async (data: {
		courseId: string
		creationDate: string
		completeDate: string
	}) => {
		try {
			setIsExporting(true);
			const blob = await ExportResultTracking(data);

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute(
				'download',
				`So_Theo_Doi_${course?.classCode || courseId}.docx`
			);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success('Tải xuất sổ theo dõi thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xuất sổ theo dõi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportDecision = async (data: any) => {
		try {
			setIsExporting(true);
			const { blob, headers } = await ExportDecision(data);

			// Determine file extension based on DecisionType
			let fileExtension = 'zip';
			let fileName = `Quyet_Dinh_${course?.classCode || courseId}`;

			// Map DecisionType to file name and extension
			const decisionType = data.decisionType || 0;

			if (decisionType === 0) {
				// All - returns zip
				fileExtension = 'zip';
			} else if (decisionType === 8) {
				// SummaryList - returns xlsx
				fileExtension = 'xlsx';
				fileName = 'DanhSachTotNghiep';
			} else {
				// Other types - return docx with specific names
				fileExtension = 'docx';
				const fileNameMap: { [key: number]: string } = {
					1: '1.QuyetDinhMoLop',
					2: '2.QuyetDinhThanhLapHDXetTN',
					3: '3.QuyetDinhThanhLapHDThiTN',
					4: '4.QuyetDinhCongNhanTN',
					5: '5.PhanCongChamThi',
					6: '6.BienBanTongHopTN',
					7: '0.BaoCaoDangKyKhoaDaoTao'
				};
				fileName = fileNameMap[decisionType] || fileName;
			}

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', `${fileName}.${fileExtension}`);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success('Tải xuất quyết định thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xuất quyết định';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};
	const openCreateStudentModal = () => {
		setSelectedStudent(null);
		setIsStudentModalOpen(true);
	};

	const openEditStudentModal = (student: any) => {
		setSelectedStudent(student);
		setIsStudentModalOpen(true);
	};

	useEffect(() => {
		if (searchTerm.trim() === '') {
			setFilteredStudents(students);
		} else {
			const term = searchTerm.toLowerCase();
			const filtered = students.filter(
				(student) =>
					student.fullName?.toLowerCase().includes(term) ||
					student.identifyNumber?.toLowerCase().includes(term) ||
					student.registerCode?.toLowerCase().includes(term)
			);
			setFilteredStudents(filtered);
		}
	}, [searchTerm, students]);

	useEffect(() => {
		setSelectedTeachers(teachers.map((t) => t.id));
	}, [teachers]);

	useEffect(() => {
		setSelectedVehicles(vehicles.map((v) => v.id));
	}, [vehicles]);

	useEffect(() => {
		fetchCourse();
		fetchDataByTab(activeTab);
	}, [courseId, activeTab]);

	const openBulkSelectModal = (type: 'teachers' | 'vehicles') => {
		if (type === 'teachers') {
			fetchAllTeachers();
		}
		if (type === 'vehicles') {
			fetchAllVehicles();
		}
		setModalType(type);
		setIsBulkSelectModalOpen(true);
	};

	const handleSaveSelection = async (selectedIds: string[]) => {
		try {
			if (modalType === 'teachers') {
				// Gọi API lưu giáo viên
				await CourseTeachingSave({
					courseId,
					teacherIds: selectedIds,
				});
				toast.success('Lưu giáo viên thành công');
				setSelectedTeachers(selectedIds);
			} else {
				// Gọi API lưu xe
				await CourseVehicleSave({
					courseId,
					vehicleIds: selectedIds,
				});
				toast.success('Lưu xe thành công');
				setSelectedVehicles(selectedIds);
			}
			// Refresh dữ liệu
			await fetchDataByTab(modalType, true);
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		}
	};

	const handleExamCreateAuto = async () => {
		try {
			if (!course) return;

			const isConfirmed = await confirm({
				title: 'Xác nhận cập nhật kỳ thi',
				description: `Bạn có chắc chắn muốn cập nhật các kỳ thi mới nhất từ cài đặt?`,
			});

			if (!isConfirmed) return;

			const res = await ExamCheck(courseId);

			let actionResults: boolean | number[] = true;

			if (
				(res.result != '' || res.result != null) &&
				res.result.length > 0
			) {
				const options = res.result.map(
					(item: { message: any; key: any }) => ({
						message: item.message,
						value: item.key,
					})
				);
				const selectedActions = await confirm({
					title: 'Chọn hành động',
					description:
						'Vui lòng chọn một hoặc nhiều hành động bên dưới:',
					options: options,
				});

				if (!selectedActions) {
					return;
				}
				actionResults = selectedActions;
			}

			setIsLoading(true);
			const response = await ExamCreateAutomatic({
				courseId,
				confirms: Array.isArray(actionResults) ? actionResults : [],
			});

			if (response.message) {
				toast.success(response.message);
			}

			await fetchDataByTab('exams', true);
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi cập nhật bài thi tự động';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
			console.error('Lỗi khi cập nhật bài thi tự động:', error);
		}
	};

	const handleSubjectSubmit = async (subjectData: any) => {
		try {
			setIsLoading(true);
			const payload = {
				...subjectData,
				id: subjectData.id || null,
				licenseClassId: null,
				courseId: courseId,
			};
			const response = await SemesterSave(payload);

			if (response.message) {
				toast.success(response.message);
			}

			await fetchTrainingPlanData();
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi lưu môn học';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
			console.error('Lỗi khi lưu môn học:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteSubject = async (id: string) => {
		try {
			setIsLoading(true);
			const response = await SemesterDelete(id);
			if (response.message) {
				toast.success(response.message);
			} else {
				toast.success('Xóa môn học thành công');
			}

			await fetchTrainingPlanData();
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xóa môn học';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
			console.error('Lỗi khi xóa môn học:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDuplicatePlan = async () => {
		try {
			if (!course) return;

			const isConfirmed = await confirm({
				title: 'Xác nhận cập nhật KHĐT',
				description: `Bạn có chắc chắn muốn cập nhật KHĐT mới nhất từ hạng đào tạo ${course.licenseClassName}?`,
			});

			if (!isConfirmed) return;

			setIsLoading(true);
			const res = await SemesterDuplicate(
				course.licenseClassId,
				courseId
			);

			if (res.message) {
				toast.success(res.message);
			} else {
				toast.success('Cập nhật KHĐT thành công');
			}
			await fetchTrainingPlanData();
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xóa môn học';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
			console.error('Lỗi khi xóa môn học:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleExportSemester = async () => {
		try {
			setIsExporting(true);

			const blob = await SemesterExport({
				courseId: courseId,
			});

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			const now = new Date();
			const timestamp = `${now.getFullYear()}${(now.getMonth() + 1)
				.toString()
				.padStart(2, '0')}${now
				.getDate()
				.toString()
				.padStart(2, '0')}-${now
				.getHours()
				.toString()
				.padStart(2, '0')}${now
				.getMinutes()
				.toString()
				.padStart(2, '0')}${now
				.getSeconds()
				.toString()
				.padStart(2, '0')}`;
			link.href = downloadUrl;
			link.setAttribute('download', `KHHT-${timestamp}.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Clean up
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			setIsExporting(false);
			toast.success('Tải xuống thành công');
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const fetchDataByTab = async (tab: TabType, forceRefresh = false) => {
		if (
			!forceRefresh &&
			dataStatus[tab] &&
			((tab === 'schedule' && schedule.length > 0) ||
				(tab === 'students' && students.length > 0) ||
				(tab === 'teachers' && teachers.length > 0) ||
				(tab === 'vehicles' && vehicles.length > 0) ||
				(tab === 'exams' && exams.length > 0) ||
				(tab === 'trainingPlan' && trainingPlanData) ||
				(tab === 'examResults' && examResults.length > 0))
		) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setIsRefreshing(true);
		try {
			switch (tab) {
				case 'schedule':
					await fetchSchedule();
					break;
				case 'students':
					await fetchStudents();
					break;
				case 'teachers':
					await fetchTeachers();
					break;
				case 'vehicles':
					await fetchVehicles();
					break;
				case 'exams':
					await fetchExams();
					break;
				case 'trainingPlan':
					await fetchTrainingPlanData();
					break;
				case 'log':
					await fetchLog();
					break;
				case 'examResults':
					await fetchExamResults();
					break;
			}
			// Đánh dấu dữ liệu đã được tải
			setDataStatus((prev) => ({ ...prev, [tab]: true }));
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi lấy dữ liệu';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setIsLoading(false);
			setIsRefreshing(false);
		}
	};

	const fetchSchedule = async () => {
		const res = await ScheduleGetPaging({ courseId: courseId });
		setSchedule(res.result.items);
	};

	const fetchStudents = async () => {
		const res = await StudentGetPaging({ courseId });
		setStudents(res.result.items);
		if (examResults.length > 0) {
			await fetchExamResults();
		}
	};

	const fetchTeachers = async () => {
		const res = await TeacherGetPaging({ courseId });
		setTeachers(res.result.items);
	};

	const fetchVehicles = async () => {
		const res = await VehicleGetPaging({ courseId });
		setVehicles(res.result.items);
	};
	const fetchExams = async () => {
		const res = await ExamGetPaging({ courseId });
		setExams(res.result.items);
		if (examResults.length > 0) {
			await fetchExamResults();
		}
	};

	const fetchTrainingPlanData = async () => {
		const response = await SemesterGetCourse(courseId);
		setTrainingPlanData(response.result);
	};
	const fetchLog = async () => {
		const response = await LogGetPaging({
			action: null,
			endDate: null,
			freeTextSearch: null,
			pageIndex: null,
			pageSize: null,
			startDate: null,
			recordId: courseId,
		});
		setLogs(response.result.items);
	};
	const fetchCourse = async (force = false) => {
		if (!courseLoaded || force) {
			setIsLoading(true);
			await CourseGet(courseId).then((res) => {
				setCourse(res.result);
				setCourseLoaded(true);
			});
		}
	};

	const fetchExamResults = async () => {
		if (students.length === 0) {
			fetchStudents();
		}
		if (examTypes.length === 0) {
			const examTypesRes = await SettingGet('ExamTypes');
			if (examTypesRes.result) {
				setExamTypes(
					examTypesRes.result.value
						.split(',')
						.map((item: string) => item.trim())
				);
			}
		}
		const resultsRes = await ExamResultGetList({ courseId });
		setExamResults(resultsRes.result);
	};

	// Chuẩn bị dữ liệu cho lịch
	const calendarEvents = schedule.map((item) => ({
		id: item.id,
		title: item.classCode,
		start: new Date(item.startDate),
		end: new Date(item.endDate),
		extendedProps: {
			type: item.type || 1,
			teacherName: `${item.teacherName}${
				item.teacherPartnerDes ? ' - ' + item.teacherPartnerDes : ''
			}`,
			vehiclePlateName: `${item.vehiclePlateName}${
				item.vehiclePartnerDes ? ' - ' + item.vehiclePartnerDes : ''
			}`,
		},
	}));

	const handleEventClick = (event: any) => {
		const scheduleId = event.id;
		const selected = schedule.find((s) => s.id === scheduleId);
		if (selected) {
			openEditModal(selected);
		}
	};

	const renderTabContent = () => {
		switch (activeTab) {
			case 'schedule':
				return (
					<>
						<div className='bg-white rounded-lg shadow-md p-6'>
							<h2 className='text-xl font-semibold text-gray-700 mb-4'>
								Lịch trình
							</h2>
							{isLoading ? (
								<p className='text-gray-500'>
									Đang tải lịch...
								</p>
							) : (
								<div className='min-h-[500px]'>
									<MinaScheduler
										events={calendarEvents}
										initialView='dayGridMonth'
										headerToolbar={{
											left: 'prev,next today',
											center: 'title',
											right: 'dayGridMonth,timeGridWeek,timeGridDay',
										}}
										onEventClick={handleEventClick}
									/>
								</div>
							)}
						</div>

						<div className='bg-white rounded-lg shadow-md p-6'>
							<h1 className='text-xl font-semibold text-gray-700 mb-4'>
								Danh sách lịch học
							</h1>
							{isLoading ? (
								<p className='text-gray-500'>
									Đang tải danh sách lịch học...
								</p>
							) : schedule.length === 0 ? (
								<p className='text-gray-500'>
									Chưa có lịch nào được tạo.
								</p>
							) : (
								<div className='overflow-x-auto'>
									<table className='min-w-full divide-y divide-gray-200'>
										<thead className='bg-gray-50'>
											<tr>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													TT | STT
												</th>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													Mã lớp
												</th>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													Ngày bắt đầu
												</th>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													Ngày kết thúc
												</th>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													Giáo viên
												</th>
												<th
													scope='col'
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
													Xe
												</th>
											</tr>
										</thead>
										<tbody className='bg-white divide-y divide-gray-200'>
											{schedule.map((item, index) => (
												<tr
													key={item.id}
													// className={"cursor-pointer"} onClick={() => openEditModal(item)}
												>
													<td className='px-6 py-4 whitespace-nowrap text-left text-sm text-gray-500'>
														<button
															onClick={() =>
																openEditModal(
																	item
																)
															}
															className='text-indigo-600 hover:text-indigo-900 mr-5'>
															<Edit></Edit>
														</button>
														{index + 1}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{item.classCode}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{formatDate(
															item.startDate,
															true
														)}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{formatDate(
															item.endDate,
															true
														)}
													</td>

													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{item.teacherName} -{' '}
														{item.teacherPartnerName ||
															''}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{item.vehiclePlateName}{' '}
														-{' '}
														{item.vehiclePartnerName ||
															''}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</>
				);
			case 'students':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<h1 className='text-xl font-semibold text-gray-700 mb-4'>
							Danh sách học viên ({course?.totalStudents})
						</h1>
						<div className='md:flex md:justify-end md:flex-wrap space-x-3 mb-4'>
							<button
								onClick={openCreateStudentModal}
								className='w-full md:w-auto flex items-center px-4 py-2  mb-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
								<Plus className={'mr-1'}></Plus> Thêm học viên
							</button>
							<button
								onClick={handleDownloadTemplate}
								className='w-full md:w-auto flex items-center px-4 py-2 mb-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700'>
								<Download className={'mr-1'}></Download> Tải mẫu
							</button>
							<button
								onClick={() =>
									document
										.getElementById('studentImport')
										?.click()
								}
								className='w-full md:w-auto flex items-center px-4 py-2 mb-2 bg-purple-600 text-white rounded-md hover:bg-purple-700'>
								<Upload className={'mr-1'}></Upload> Nhập DSHV
							</button>
							<button
								onClick={handleStudentExport}
								className='w-full md:w-auto flex items-center px-4 py-2 mb-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Download className={'mr-1'}></Download> Xuất
								DSHV
							</button>
							<input
								id='studentImport'
								type='file'
								accept='.xlsx,.xls,.xlsm'
								onChange={async (e) => {
									const file = e.target.files?.[0];
									if (file) {
										const result =
											await handleStudentImport(file);
										e.target.value = ''; // Reset input
									}
								}}
								className='hidden'
							/>
							<input
								type='text'
								placeholder='Tìm kiếm học viên...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className='w-full md:w-auto px-3 py-2 mb-2 border border-gray-300 rounded-md '
							/>
						</div>
						{isLoading ? (
							<p className='text-gray-500'>
								Đang tải danh sách học viên...
							</p>
						) : filteredStudents.length === 0 ? (
							<p className='text-gray-500'>
								{searchTerm
									? 'Không tìm thấy học viên phù hợp'
									: 'Chưa có học viên nào.'}
							</p>
						) : (
							<div className='overflow-x-auto'>
								<table className='min-w-full divide-y divide-gray-200'>
									<thead className='bg-gray-50'>
										<tr>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												STT
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Họ tên
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Mã ĐK
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Số CC
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												CCCD/HC
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Ngày sinh
											</th>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Thao tác
											</th>
										</tr>
									</thead>
									<tbody className='bg-white divide-y divide-gray-200'>
										{filteredStudents.map(
											(student, index) => (
												<tr key={student.id}>
													<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
														{index + 1}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{student.fullName}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{student.registerCode}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{
															student.certificateNumber
														}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{student.identifyNumber}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
														{formatDate(
															student.birthDate
														)}
													</td>
													<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
														<button
															onClick={() =>
																openEditStudentModal(
																	student
																)
															}
															className='text-indigo-600 hover:text-indigo-900 mr-2'>
															<Edit />
														</button>
													</td>
												</tr>
											)
										)}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);
			case 'teachers':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<h1 className='text-xl font-semibold text-gray-700 mb-4'>
							Danh sách giáo viên
						</h1>
						<div className={'flex justify-end space-x-3 mb-4'}>
							<button
								onClick={() => setIsLinkModalOpen(true)}
								className='flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Link className={'mr-1'} size={18}></Link>
								Liên kết GV-XE
							</button>
							<button
								onClick={handleExportTeachingAssignment}
								disabled={exportingAssignment}
								className='flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50'>
								<Download className={'mr-1'} size={18}></Download>
								{exportingAssignment ? 'Đang tải...' : 'Tải DS Phân công'}
							</button>
							<button
								onClick={() => openBulkSelectModal('teachers')}
								className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
								<UserRoundPen className={'mr-1'}></UserRoundPen>{' '}
								Quản lý GV
							</button>
						</div>

						{isLoading ? (
							<p className='text-gray-500'>
								Đang tải danh sách giáo viên...
							</p>
						) : teachers.length === 0 ? (
							<p className='text-gray-500'>
								Chưa có giáo viên nào.
							</p>
						) : (
							<div className='overflow-x-auto'>
								<table className='min-w-full divide-y divide-gray-200'>
									<thead className='bg-gray-50'>
										<tr>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												STT
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Họ tên
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Hạng
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Số điện thoại
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Đơn vị
											</th>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Thao tác
											</th>
										</tr>
									</thead>
									<tbody className='bg-white divide-y divide-gray-200'>
										{teachers.map((teacher, index) => (
											<tr key={teacher.id}>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													{index + 1}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-md text-gray-500'>
													<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10'>
														{teacher.fullName}
													</span>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-blue-700 inset-ring inset-ring-blue-700/10'>
														{
															teacher.licenseClassName
														}
													</span>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{teacher.phoneNumber ??
														'N/A'}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{teacher.partnerName ? (
														<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10'>
															{
																teacher.partnerName
															}
														</span>
													) : (
														''
													)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													<button
														className='text-red-600 hover:text-red-900'
														onClick={() =>
															handleDeleteItem(
																teacher.id,
																'teacher'
															)
														}>
														<Trash className='inline mr-1' />{' '}
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);
			case 'vehicles':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<h1 className='text-xl font-semibold text-gray-700 mb-4'>
							Danh sách phương tiện
						</h1>
						<div className={'flex justify-end space-x-3 mb-4'}>
							<button
								onClick={() => setIsLinkModalOpen(true)}
								className='flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Link className={'mr-1'} size={18}></Link>
								Liên kết GV-XE
							</button>
							<button
								onClick={() => openBulkSelectModal('vehicles')}
								className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
								<Car className={'mr-1'}></Car>Quản lý XE
							</button>
						</div>
						{isLoading ? (
							<p className='text-gray-500'>
								Đang tải danh sách phương tiện...
							</p>
						) : vehicles.length === 0 ? (
							<p className='text-gray-500'>
								Chưa có phương tiện nào.
							</p>
						) : (
							<div className='overflow-x-auto'>
								<table className='min-w-full divide-y divide-gray-200'>
									<thead className='bg-gray-50'>
										<tr>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												STT
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Biển số
											</th>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Hạng
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Đơn vị
											</th>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Thao tác
											</th>
										</tr>
									</thead>
									<tbody className='bg-white divide-y divide-gray-200'>
										{vehicles.map((vehicle, index) => (
											<tr key={vehicle.id}>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													{index + 1}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10'>
														{vehicle.plateNumber}
													</span>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10'>
														{
															vehicle.licenseClassName
														}
													</span>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													<span className='inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10'>
														{vehicle.partnerName ||
															''}
													</span>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													<button
														className='text-red-600 hover:text-red-900'
														onClick={() =>
															handleDeleteItem(
																vehicle.id,
																'vehicle'
															)
														}>
														<Trash className='inline mr-1' />{' '}
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);

			case 'documents':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<h1 className='text-xl font-semibold text-gray-700 mb-4'>
							Tài liệu
						</h1>
						{courseId ? (
							<DocumentManager courseId={courseId} />
						) : (
							''
						)}
					</div>
				);
			case 'exams':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<h1 className='text-xl font-semibold text-gray-700 mb-4'>
							Danh sách kỳ thi
						</h1>

						<div className='flex justify-end space-x-3 mb-4'>
							<button
								onClick={handleExamCreateAuto}
								className='flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-green-700'>
								<Files className={'mr-1'} />
								Cập nhật kỳ thi
							</button>
						</div>

						{isLoading ? (
							<p className='text-gray-500'>
								Đang tải danh sách kỳ thi...
							</p>
						) : exams.length === 0 ? (
							<p className='text-gray-500'>Chưa có kỳ thi nào.</p>
						) : (
							<div className='overflow-x-auto'>
								<table className='min-w-full divide-y divide-gray-200'>
									<thead className='bg-gray-50'>
										<tr>
											<th className='px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
												STT
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Tên kỳ thi
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Ngày bắt đầu
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Ngày kết thúc
											</th>
											<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
												Xe
											</th>
										</tr>
									</thead>
									<tbody className='bg-white divide-y divide-gray-200'>
										{exams.map((exam, index) => (
											<tr key={exam.id}>
												<td className='px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500'>
													{index + 1}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{exam.name}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{formatDate(
														exam.startDate,
														true
													)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{formatDate(
														exam.endDate,
														true
													)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{exam.vehiclePlateName ||
														'Không có'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);
			case 'examResults':
				return (
					<div className='bg-white rounded-lg shadow-md p-6'>
						<div className='flex justify-end space-x-3 mb-4'>
							<button
								onClick={handleExamCreateAuto}
								className='flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700'>
								<Files className={'mr-1'} />
								Cập nhật kỳ thi
							</button>

							<button
								onClick={() =>
									document
										.getElementById('examResultImport')
										?.click()
								}
								className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
								<Upload className={'mr-1'} />
								Nhập điểm Online
							</button>

							<input
								id='examResultImport'
								type='file'
								accept='.xlsx,.xls,.xlsm'
								onChange={async (e) => {
									const file = e.target.files?.[0];
									if (file) {
										const result =
											await handleExamResultImport(file);
										e.target.value = '';
									}
								}}
								className='hidden'
							/>

							<button
								onClick={handleExportResult}
								className='flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Download className={'mr-1'} />
								Xuất bảng điểm
							</button>
						</div>
						<ExamResultsTab
							courseId={courseId}
							students={students}
							examTypes={examTypes}
							examResults={examResults}
							onSave={handleExamResultSave}
						/>
					</div>
				);
			case 'trainingPlan':
				return (
					<div className='bg-white rounded-lg shadow-md p-6 relative'>
						<div className={'md:flex md:justify-end md:flex-wrap'}>
							<button
								onClick={handleDuplicatePlan}
								className='flex items-center mr-2 mb-3 w-full md:w-auto px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700'>
								<Files className={'mr-1'} />
								Cập nhật KHĐT
							</button>

							<button
								onClick={() => setIsExportGAModalOpen(true)}
								className='flex items-center mr-2 mb-3 w-full md:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Download className={'mr-1'} />
								Xuất giáo án
							</button>

							<button
								onClick={() => setIsExportAllModalOpen(true)}
								className='flex items-center mr-2 mb-3 w-full md:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
								<Download className={'mr-1'} />
								Xuất BC1
							</button>

							<button
								onClick={() => setIsExportResultTrackingModalOpen(true)}
								className='flex items-center mr-2 mb-3 w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
								<Download className={'mr-1'} />
								Xuất Sổ theo dõi
							</button>

							<button
								onClick={() => setIsExportDecisionModalOpen(true)}
								className='flex items-center mb-3 w-full md:w-auto px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700'>
								<Download className={'mr-1'} />
								Xuất Quyết định
							</button>
						</div>

						{trainingPlanData ? (
							<SubjectPlanningPage
								data={trainingPlanData}
								onSubjectSubmit={handleSubjectSubmit}
								onRefresh={fetchTrainingPlanData}
								onDeleteSubject={handleDeleteSubject}
								onExport={handleExportSemester}
							/>
						) : (
							<div className='p-4'>
								<div className='bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded'>
									Không có kế hoạch đào tạo
								</div>
							</div>
						)}
					</div>
				);
			case 'log':
				return (
					<div className='bg-white rounded-lg shadow-md p-6 relative'>
						<div className='w-full overflow-x-auto'>
							<table className='min-w-max w-full divide-y divide-gray-200'>
								<thead className='bg-gray-50'>
								<tr>
									<th className='px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider'>
										Hành động
									</th>
									<th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										Thao tác
									</th>
									<th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										Thời gian
									</th>
									<th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
										Thực hiện bởi
									</th>
								</tr>
								</thead>
								<tbody className='bg-white divide-y divide-gray-200'>
								{logs.map((row, idx) => (
									<tr
										key={idx}
										className={`border-t hover:bg-gray-50 transition`}>
										<td className='p-3'>
											{row.detail}
										</td>
										<td className='p-3'>
												<span
													className={`badge badge-pill ${
														row.action == 'Create' || row.action == 'Import' || row.action == 'Export'
															? 'badge-success'
															: row.action ==
															'Update'
																? 'badge-warning'
																: 'badge-danger'
													}`}>
													{row.action}
												</span>
										</td>
										<td className='p-3'>
											{moment(
												row.creationDate
											).format('DD/MM/YYYY HH:mm:ss')}
										</td>
										<td className='p-3'>
											{row.fullName}
										</td>
									</tr>
								))}
								</tbody>
							</table>
						</div>
					</div>
				)
			default:
				return null;
		}
	};

	return (
		<div className='mx-auto px-4 py-8'>
			<h1 className='text-3xl font-bold text-gray-800 mb-5'>
				<div className={'flex items-center'}>
					Chi tiết khóa học - {course?.name}{' '}
					<PencilLine className={'ml-2'} />
				</div>
			</h1>

			{/* Tab navigation */}
			<div className='border-b border-gray-200 mb-6'>
				<div className='flex flex-wrap md:flex-nowrap overflow-x-auto md:overflow-visible'>
					{[
						{ key: 'schedule', label: 'Lịch học' },
						{
							key: 'students',
							label: `Học viên (${
								students.length > 0
									? students.length
									: course?.totalStudents || 0
							})`,
						},
						{
							key: 'teachers',
							label: `Giáo viên (${
								teachers.length > 0
									? teachers.length
									: course?.totalTeachers || 0
							})`,
						},
						{
							key: 'vehicles',
							label: `Xe (${
								vehicles.length > 0
									? vehicles.length
									: course?.totalVehicles || 0
							})`,
						},
						{
							key: 'exams',
							label: `Kỳ thi (${
								exams.length > 0
									? exams.length
									: course?.totalExams || 0
							})`,
						},
						{ key: 'examResults', label: 'Nhập điểm' },
						{ key: 'documents', label: 'Tài liệu' },
						{ key: 'trainingPlan', label: 'Kế hoạch ĐT' },
						{ key: 'log', label: 'Nhật ký' },
					].map((tab) => (
						<button
							key={tab.key}
							onClick={
								// @ts-ignore
								() => setActiveTab(tab.key)
							}
							className={`whitespace-nowrap py-2 px-3 text-xs md:text-sm font-medium ${
								activeTab === tab.key
									? 'border-b-2 border-blue-500 text-blue-600'
									: 'text-gray-500 hover:text-gray-700'
							}`}>
							{tab.label}
						</button>
					))}
				</div>
			</div>

			{activeTab === 'schedule' && (
				<div className={'md:flex md:justify-between mb-4'}>
					<button
						onClick={openCreateModal}
						className='flex items-center w-full md:w-auto px-4 py-2 mb-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
						<Plus className={'mr-1'}></Plus> Thêm lịch học
					</button>

					<button
						onClick={handleExportSchedule}
						className='flex items-center w-full md:w-auto px-4 py-2 mb-2 bg-green-600 text-white rounded-md hover:bg-green-700'>
						<Download className={'mr-1'}></Download> Xuất tiến độ
					</button>
				</div>
			)}

			{isRefreshing && <LoadingOverlay message='Đang tải dữ liệu...' />}

			{isExporting && <DownloadingOverlay />}

			<div className='grid grid-cols-1 gap-8'>{renderTabContent()}</div>

			<ScheduleModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				courseId={courseId}
				schedule={selectedSchedule}
				refreshData={() => fetchSchedule()}
			/>

			<BulkSelectModal
				isOpen={isBulkSelectModalOpen}
				onClose={() => setIsBulkSelectModalOpen(false)}
				title={modalType === 'teachers' ? 'Chọn giáo viên' : 'Chọn xe'}
				items={
					modalType === 'teachers'
						? allTeachers.map((t) => ({
								id: t.id,
								name:
									t.fullName +
									(t.partnerName
										? ` - ${t.partnerName}`
										: ''),
								licenseClassName: t.licenseClassName,
						  }))
						: allVehicles.map((v) => ({
								id: v.id,
								name:
									v.plateNumber +
									(v.partnerName
										? ` - ${v.partnerName}`
										: ''),
								licenseClassName: v.licenseClassName,
						  }))
				}
				itemNone={
					modalType === 'teachers'
						? allTeacherNoEligible.map((t) => ({
								id: t.id,
								name:
									t.fullName +
									(t.partnerName
										? ` - ${t.partnerName}`
										: ''),
								licenseClassName: t.licenseClassName,
						  }))
						: allVehicleNoEligible.map((v) => ({
								id: v.id,
								name:
									v.plateNumber +
									(v.partnerName
										? ` - ${v.partnerName}`
										: ''),
								licenseClassName: v.licenseClassName,
						  }))
				}
				onSubmit={handleSaveSelection}
				onDelete={handleDeleteItem}
				selectedIds={
					modalType === 'teachers'
						? selectedTeachers
						: selectedVehicles
				}
				showLicenseClass={true}
			/>

			<SaveStudentModal
				isOpen={isStudentModalOpen}
				onClose={() => setIsStudentModalOpen(false)}
				courseId={courseId}
				student={selectedStudent}
				refreshData={fetchStudents}
			/>

			<SaveExamModal
				isOpen={isExamModalOpen}
				onClose={() => {
					setIsExamModalOpen(false);
					setSelectedExam(null);
				}}
				courseId={courseId}
				exam={selectedExam}
				refreshData={() => fetchDataByTab('exams', true)}
			/>

			<ExportGAModal
				isOpen={isExportGAModalOpen}
				onClose={() => setIsExportGAModalOpen(false)}
				onExport={handleExportGA}
				isLoading={isExporting}
				teamCount={course?.teamCount || 0}
			/>
			<ExportAllModal
				isOpen={isExportAllModalOpen}
				onClose={() => setIsExportAllModalOpen(false)}
				onExport={exportData => handleExportAll(exportData)}
				courseId={courseId}
				isLoading={isExporting}
			/>
			<LinkTeacherVehicleModal
				visible={isLinkModalOpen}
				onClose={() => {
					setIsLinkModalOpen(false);
					// Refresh both teachers and vehicles data after modal closes
					fetchTeachers();
					fetchVehicles();
				}}
				courseId={courseId}
			/>
			<ExportResultTrackingModal
				isOpen={isExportResultTrackingModalOpen}
				onClose={() => setIsExportResultTrackingModalOpen(false)}
				onExport={handleExportResultTracking}
				courseId={courseId}
				isLoading={isExporting}
			/>
			<ExportDecisionModal
				isOpen={isExportDecisionModalOpen}
				onClose={() => setIsExportDecisionModalOpen(false)}
				onExport={handleExportDecision}
				courseId={courseId}
				isLoading={isExporting}
			/>
			<ErrorModal
				isOpen={errorModalOpen}
				message={errorMessage}
				onClose={() => setErrorModalOpen(false)}
			/>
		</div>
	);
}
